import React, { useEffect, useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { BreathingSession } from '../../../Model/Explore/BreathingSession';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { Theme } from '../../../Theme/Theme';

const MIN_SCALE = 0.5;
const MAX_SCALE = 1.0;

function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

function computeScale(pos: number, grow: number, hold: number, shrink: number): number {
  if (pos < grow) {
    return MIN_SCALE + (MAX_SCALE - MIN_SCALE) * easeInOutSine(pos / grow);
  } else if (pos < grow + hold) {
    return MAX_SCALE;
  } else {
    return MAX_SCALE - (MAX_SCALE - MIN_SCALE) * easeInOutSine((pos - grow - hold) / shrink);
  }
}

function getPhase(pos: number, grow: number, hold: number): 'inhale' | 'hold' | 'exhale' {
  if (pos < grow) return 'inhale';
  if (pos < grow + hold) return 'hold';
  return 'exhale';
}

function getCountdown(pos: number, grow: number, hold: number, shrink: number, phase: string): number {
  if (phase === 'inhale') return Math.ceil(grow - pos);
  if (phase === 'hold') return Math.ceil(grow + hold - pos);
  return Math.ceil(grow + hold + shrink - pos);
}

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 40px rgba(0, 221, 162, 0.3), 0 0 80px rgba(0, 221, 162, 0.1); }
  50% { box-shadow: 0 0 60px rgba(0, 221, 162, 0.5), 0 0 120px rgba(0, 221, 162, 0.2); }
`;

const idlePulse = keyframes`
  0%, 100% { transform: scale(${MIN_SCALE}); }
  50% { transform: scale(${MIN_SCALE + 0.05}); }
`;

const Container = styled.div<{ $bgImage?: string }>`
  position: relative;
  width: 100%;
  min-height: 75vh;
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ $bgImage }) =>
    $bgImage
      ? `url(${$bgImage}) center/cover no-repeat`
      : 'linear-gradient(180deg, #0a1628 0%, #0e3a4f 50%, #0a2e42 100%)'};
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.25);

  @media (max-width: 768px) {
    min-height: 80vh;
    border-radius: 20px;
  }
`;

const DarkOverlay = styled.div<{ $hasImage: boolean }>`
  position: absolute;
  inset: 0;
  background: ${({ $hasImage }) => ($hasImage ? 'rgba(0, 0, 0, 0.5)' : 'transparent')};
  z-index: 1;
`;

const CenterArea = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 360px;
  height: 360px;
  z-index: 2;

  @media (max-width: 768px) {
    width: 310px;
    height: 310px;
  }
`;

const OrbContainer = styled.div<{ $idle: boolean }>`
  position: absolute;
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform;
  animation: ${({ $idle }) => ($idle ? idlePulse : 'none')} 4s ease-in-out infinite;

  @media (max-width: 768px) {
    width: 260px;
    height: 260px;
  }
`;

const OrbGlow = styled.div`
  position: absolute;
  width: 150%;
  height: 150%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 221, 162, 0.2) 0%, rgba(0, 180, 140, 0.08) 50%, transparent 70%);
  filter: blur(30px);
`;

const OrbOuter = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%,
    rgba(0, 221, 162, 0.15) 0%,
    rgba(0, 200, 155, 0.08) 60%,
    rgba(0, 180, 140, 0.03) 100%);
  border: 1.5px solid rgba(0, 221, 162, 0.12);
`;

const OrbMiddle = styled.div`
  position: absolute;
  width: 82%;
  height: 82%;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%,
    rgba(0, 221, 162, 0.35) 0%,
    rgba(0, 200, 155, 0.2) 40%,
    rgba(0, 180, 140, 0.08) 80%);
  border: 1.5px solid rgba(0, 221, 162, 0.18);
`;

const OrbInner = styled.div`
  position: absolute;
  width: 58%;
  height: 58%;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%,
    rgba(0, 221, 162, 0.55) 0%,
    rgba(0, 221, 162, 0.35) 40%,
    rgba(0, 200, 155, 0.15) 100%);
`;

const OrbCore = styled.div`
  position: absolute;
  width: 32%;
  height: 32%;
  border-radius: 50%;
  background: radial-gradient(circle at 45% 40%,
    rgba(180, 255, 230, 0.6) 0%,
    rgba(0, 221, 162, 0.45) 50%,
    rgba(0, 200, 155, 0.2) 100%);
`;

const TextOverlay = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
  pointer-events: none;
`;

const PhaseText = styled.div<{ $visible: boolean }>`
  font-size: 22px;
  font-weight: 300;
  color: white;
  text-transform: uppercase;
  letter-spacing: 4px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.4s ease;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 17px;
    letter-spacing: 3px;
  }
`;

const CountdownNumber = styled.div<{ $visible: boolean }>`
  font-size: 52px;
  font-weight: 200;
  color: white;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  margin-top: 8px;
  opacity: ${({ $visible }) => ($visible ? 0.9 : 0)};
  transition: opacity 0.4s ease;

  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const ProgressRingSVG = styled.svg`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
`;

const PlayButton = styled.button`
  position: absolute;
  width: 90px;
  height: 90px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;
  animation: ${pulseGlow} 2.5s ease-in-out infinite;
  pointer-events: auto;

  &:hover {
    transform: scale(1.1);
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const PlayIcon = styled.div`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 16px 0 16px 26px;
  border-color: transparent transparent transparent white;
  margin-left: 6px;
`;

const BottomArea = styled.div`
  position: absolute;
  bottom: 36px;
  left: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  z-index: 3;

  @media (max-width: 768px) {
    bottom: 24px;
    left: 16px;
    right: 16px;
  }
`;

const InstructionText = styled.div`
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 14px 28px;
  color: white;
  font-size: 15px;
  font-weight: 400;
  text-align: center;
  line-height: 1.5;
  max-width: 460px;

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 20px;
  }
`;

const TopBar = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 3;
`;

const SessionTimer = styled.div`
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 8px 14px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;

const AudioToggle = styled.button<{ $enabled: boolean }>`
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  border: 1px solid ${({ $enabled }) => ($enabled ? 'rgba(0, 221, 162, 0.4)' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 12px;
  padding: 8px 12px;
  color: ${({ $enabled }) => ($enabled ? 'rgba(0, 221, 162, 0.9)' : 'rgba(255, 255, 255, 0.5)')};
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: rgba(0, 0, 0, 0.4);
    border-color: ${({ $enabled }) => ($enabled ? 'rgba(0, 221, 162, 0.6)' : 'rgba(255, 255, 255, 0.3)')};
  }
`;

const AudioToggleLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const StopButton = styled.button`
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 10px 28px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const CompletedIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0, 221, 162, 0.2);
  border: 2px solid rgba(0, 221, 162, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.Colors.primary};
  font-size: 28px;
  margin-bottom: 8px;
`;

const CompletedText = styled.div`
  font-size: 18px;
  font-weight: 300;
  color: white;
  text-align: center;
`;

export type BreathingSoundsAndAnimationsProps = {
  activeSession: BreathingSession;
  onStart?: () => void;
  onStop?: () => void;
  growTime: number;
  holdTime: number;
  shrinkTime: number;
  onEnd?: () => void;
  backgroundImage?: string;
};

export const BreathingSoundsAndAnimations: React.FC<BreathingSoundsAndAnimationsProps> = observer(
  ({ activeSession, onStart, onStop, growTime, holdTime, shrinkTime, onEnd, backgroundImage }) => {
    const { t } = useTrans();
    const orbRef = useRef<HTMLDivElement>(null);
    const lastPhaseRef = useRef('');
    const completedRef = useRef(false);

    // Pre-create audio objects
    const inhaleSoundRef = useRef<HTMLAudioElement | null>(null);
    const holdSoundRef = useRef<HTMLAudioElement | null>(null);
    const exhaleSoundRef = useRef<HTMLAudioElement | null>(null);
    const endSoundRef = useRef<HTMLAudioElement | null>(null);

    const [phase, setPhase] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [progress, setProgress] = useState(0);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [audioCuesEnabled, setAudioCuesEnabled] = useState(true);
    const audioCuesRef = useRef(true);

    const toggleAudioCues = useCallback(() => {
      setAudioCuesEnabled((prev) => {
        const next = !prev;
        audioCuesRef.current = next;
        return next;
      });
    }, []);

    const isActive = activeSession.active;
    const isCompleted = activeSession.completed;
    const cycles = activeSession.totalCycles;
    const cycleTime = growTime + holdTime + shrinkTime;
    const totalSessionTime = cycleTime * cycles;

    const ringRadius = 160;
    const ringCircumference = 2 * Math.PI * ringRadius;

    // Init audio
    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      inhaleSoundRef.current = new Audio(require('../../../Assets/Audio/Breathing/inhale.mp3'));
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      holdSoundRef.current = new Audio(require('../../../Assets/Audio/Breathing/hold.mp3'));
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      exhaleSoundRef.current = new Audio(require('../../../Assets/Audio/Breathing/exhale.mp3'));
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      endSoundRef.current = new Audio(require('../../../Assets/Audio/Breathing/end.mp3'));

      return () => {
        [inhaleSoundRef, holdSoundRef, exhaleSoundRef, endSoundRef].forEach((ref) => {
          if (ref.current) {
            ref.current.pause();
            ref.current = null;
          }
        });
      };
    }, []);

    // Reset on new session
    useEffect(() => {
      if (!isActive && !isCompleted) {
        setPhase('');
        setCountdown(0);
        setProgress(0);
        setElapsedMs(0);
        lastPhaseRef.current = '';
        completedRef.current = false;
        if (orbRef.current) {
          orbRef.current.style.transform = '';
        }
      }
    }, [isActive, isCompleted]);

    // Animation loop
    useEffect(() => {
      if (!isActive || !activeSession.startDateTime) return;

      let animId: number;
      const startMs = activeSession.startDateTime.getTime();
      let lastCountdownVal = -1;

      const tick = () => {
        const elapsed = (Date.now() - startMs) / 1000;

        if (elapsed >= totalSessionTime) {
          // Session ended
          if (orbRef.current) {
            orbRef.current.style.transform = `scale(${MIN_SCALE})`;
          }
          setProgress(1);
          if (!completedRef.current) {
            completedRef.current = true;
            if (audioCuesRef.current && endSoundRef.current) {
              endSoundRef.current.currentTime = 0;
              endSoundRef.current.play().catch(() => {});
            }
            onEnd?.();
          }
          return;
        }

        const pos = elapsed % cycleTime;
        const scale = computeScale(pos, growTime, holdTime, shrinkTime);
        const curPhase = getPhase(pos, growTime, holdTime);
        const curCountdown = getCountdown(pos, growTime, holdTime, shrinkTime, curPhase);

        // Smooth orb update (60fps via ref) - no box-shadow animation to avoid artifacts
        if (orbRef.current) {
          orbRef.current.style.transform = `scale(${scale})`;
        }

        // Phase change: update text + play sound (only if audio cues enabled)
        if (curPhase !== lastPhaseRef.current) {
          lastPhaseRef.current = curPhase;
          setPhase(curPhase);
          if (audioCuesRef.current) {
            const audioRef =
              curPhase === 'inhale' ? inhaleSoundRef : curPhase === 'hold' ? holdSoundRef : exhaleSoundRef;
            if (audioRef.current) {
              audioRef.current.currentTime = 0;
              audioRef.current.play().catch(() => {});
            }
          }
        }

        // Countdown/progress: only update state when value changes
        if (curCountdown !== lastCountdownVal) {
          lastCountdownVal = curCountdown;
          setCountdown(curCountdown);
          setProgress(Math.min(1, elapsed / totalSessionTime));
          setElapsedMs(Math.floor(elapsed * 1000));
        }

        animId = requestAnimationFrame(tick);
      };

      animId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(animId);
    }, [isActive, activeSession.startDateTime, cycleTime, growTime, holdTime, shrinkTime, totalSessionTime, onEnd]);

    const getPhaseLabel = useCallback(
      (p: string) => {
        switch (p) {
          case 'inhale':
            return t('explore.breathing.commands.inhale', { defaultValue: 'Breathe In' });
          case 'hold':
            return t('explore.breathing.commands.hold', { defaultValue: 'Hold' });
          case 'exhale':
            return t('explore.breathing.commands.exhale', { defaultValue: 'Breathe Out' });
          default:
            return '';
        }
      },
      [t],
    );

    const formatTime = (ms: number) => {
      const sec = Math.floor(ms / 1000);
      return `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}`;
    };

    const progressOffset = ringCircumference * (1 - progress);

    return (
      <Container $bgImage={backgroundImage}>
        <DarkOverlay $hasImage={!!backgroundImage} />

        <TopBar>
          {isActive ? (
            <SessionTimer>
              {formatTime(elapsedMs)} / {formatTime(totalSessionTime * 1000)}
            </SessionTimer>
          ) : (
            <div />
          )}
          <AudioToggle $enabled={audioCuesEnabled} onClick={toggleAudioCues}>
            {audioCuesEnabled ? '\u{1F50A}' : '\u{1F507}'}
            <AudioToggleLabel>{audioCuesEnabled ? 'ON' : 'OFF'}</AudioToggleLabel>
          </AudioToggle>
        </TopBar>

        <CenterArea>
          {isActive && (
            <ProgressRingSVG viewBox="0 0 340 340">
              <circle cx="170" cy="170" r={ringRadius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={2} />
              <circle
                cx="170"
                cy="170"
                r={ringRadius}
                fill="none"
                stroke={Theme.Colors.primary}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeDasharray={ringCircumference}
                strokeDashoffset={progressOffset}
                transform="rotate(-90 170 170)"
                style={{ transition: 'stroke-dashoffset 0.5s linear' }}
              />
            </ProgressRingSVG>
          )}

          <OrbContainer ref={orbRef} $idle={!isActive && !isCompleted}>
            <OrbGlow />
            <OrbOuter />
            <OrbMiddle />
            <OrbInner />
            <OrbCore />
          </OrbContainer>

          <TextOverlay>
            {!isActive && (
              <PlayButton onClick={onStart}>
                <PlayIcon />
              </PlayButton>
            )}

            {isActive && (
              <>
                <PhaseText $visible={!!phase}>{getPhaseLabel(phase)}</PhaseText>
                <CountdownNumber $visible={!!phase}>{countdown}</CountdownNumber>
              </>
            )}

            {isCompleted && (
              <>
                <CompletedIcon>&#10003;</CompletedIcon>
                <CompletedText>
                  {t('explore.breathing.sessionCompleted', { defaultValue: 'Well done' })}
                </CompletedText>
              </>
            )}
          </TextOverlay>
        </CenterArea>

        <BottomArea>
          {!isActive && !isCompleted && (
            <InstructionText>
              {t('explore.breathing.introduction', {
                defaultValue: 'Relax your muscles, stay still and focus on your breath. Press play to begin.',
              })}
            </InstructionText>
          )}

          {isActive && onStop && (
            <StopButton onClick={onStop}>
              {t('explore.breathing.stopBreathingSession', { defaultValue: 'Stop session' })}
            </StopButton>
          )}

          {isCompleted && (
            <InstructionText>
              {t('explore.breathing.sessionFinishedMsg', {
                defaultValue:
                  'These {{totalBreaths}} deep breaths can help reduce your stress level and improve your focus throughout the day.',
                totalBreaths: activeSession.totalCycles,
              })}
            </InstructionText>
          )}
        </BottomArea>
      </Container>
    );
  },
);
